import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    HStack,
    Stack,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { ROUTES } from "constants/routes-path";
import { checkErrors, hasError } from "utils/forms-utils";
import { isoToBrFormat } from "utils/utils";
import { permitteesService } from "services/permitteesService";
import { documentsPermitteesService } from "services/documentsPermitteesService";
import { DocumentPermitteeType } from "constants/documents";
import { FileAttachmentMultipleInput } from "components/FileAttachmentInput/FileAttachmentMultipleInput";
import PermitteesDocumentsListPageCardHeader from "./PermitteesDocumentsListPageCardHeader";
import { downloadFile, printFile } from "../../Documents/DocumentUtils";
import { documentsService } from "../../../../services/documentsService";
import {
    getLicenseNumberFromContent,
    getTaximeterNumberFromContent,
    updateContentLicenseNumber,
    updateContentTaximeterNumber
} from "utils/document-utils";
import moment from "moment";
import "moment/locale/pt-br";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

function PermitteesDocumentsNew() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const showToast = useCustomToast();
    const attachmentRef = useRef(undefined);

    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const [searchParams] = useSearchParams();
    const parentId = searchParams.get("parent_id");
    const [permitteeData, setPermitteeData] = useState(undefined);
    const [formData, setFormData] = useState(undefined);
    const parentType = 'documento-permissionarios'

    const documentTypes = useMemo(() => {
        if (permitteeData?.cab) {
            return [
                DocumentPermitteeType.LICENCE,
                DocumentPermitteeType.FIRST_REGISTRATION,
                DocumentPermitteeType.LICENSE_RENEWAL,
                DocumentPermitteeType.TAXIMETER_INSTALLATION,
                DocumentPermitteeType.TAXIMETER_VERIFICATION,
                DocumentPermitteeType.IPI_EXEMPTION,
                DocumentPermitteeType.ICMS_EXEMPTION,
                DocumentPermitteeType.OWNERSHIP_TRANSFER,
                DocumentPermitteeType.CHANGE_TO_RENTAL,
                DocumentPermitteeType.CHANGE_TO_PRIVATE,
                DocumentPermitteeType.PERMIT_RENEWAL,
            ];
        }

        if (permitteeData?.bus || permitteeData?.motocab) {
            return [
                DocumentPermitteeType.LICENCE,
                DocumentPermitteeType.FIRST_REGISTRATION,
                DocumentPermitteeType.LICENSE_RENEWAL,
                DocumentPermitteeType.OWNERSHIP_TRANSFER,
                DocumentPermitteeType.CHANGE_TO_RENTAL,
                DocumentPermitteeType.CHANGE_TO_PRIVATE,
                DocumentPermitteeType.PERMIT_RENEWAL,
            ];
        }

        if (permitteeData?.tourism) {
            return [
                DocumentPermitteeType.LICENCE,
                DocumentPermitteeType.CHANGE_TO_RENTAL,
                DocumentPermitteeType.CHANGE_TO_PRIVATE,
            ];
        }

        if (permitteeData?.school_transport) {
            return [
                DocumentPermitteeType.LICENCE,
                DocumentPermitteeType.FIRST_REGISTRATION,
                DocumentPermitteeType.LICENSE_RENEWAL,
                DocumentPermitteeType.PERMIT_RENEWAL,
                DocumentPermitteeType.CHANGE_TO_RENTAL,
                DocumentPermitteeType.CHANGE_TO_PRIVATE,
            ];
        }

        return [];
    }, [permitteeData]);

    const [fileAttachmentName, setAttachmentFileName] = useState(null);
    const [inputError, setInputError] = useState({});

    const ids = useMemo(() => ({
        content: "conteudo",
        documentType: "type",
        documentExpirationDate: "expiration-date",
        documentObservations: "observations",
        documentFiles: "files",
        inspectionLicenseNumber: "inspection-license-number",
        inspectionTaximeterNumber: "inspection-taximeter-number",
        taximeterBrand: "taximeter-brand",
        taximeterModel: "taximeter-model",
        taximeterNumber: "taximeter-number"
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => {
        return {
            [ids.content]: {
                value: data?.conteudo || "",
                validations: [],
            },
            [ids.documentType]: {
                value: data?.tipo || "",
                validations: id ? [] : [{ required: true }],
            },
            [ids.documentExpirationDate]: {
                value: data?.data_validade,
                validations: (
                    data?.tipo === DocumentPermitteeType.LICENCE.value ||
                    data?.tipo === DocumentPermitteeType.PERMIT_RENEWAL.value
                ) ? [{ required: true }] : [],
            },
            [ids.documentObservations]: {
                value: data?.observacoes || "",
                validations: [],
            },
            [ids.documentFiles]: {
                value: data?.anexos || [],
                validations: [],
            },
            [ids.taximeterBrand]: {
                value: data?.marca_taximetro || "",
                validations: [],
            },
            [ids.taximeterModel]: {
                value: data?.modelo_taximetro || "",
                validations: [],
            },
            [ids.taximeterNumber]: {
                value: data?.numero_taximetro || "",
                validations: [],
            },
        };
    };

    const fetchData = async () => {
        try {
            if (id) {
                const documentDetails = await documentsPermitteesService.find(id);
                setAttachmentFileName(documentDetails.anexos || []);
                setFormData(initFormData(documentDetails));
            } else {
                setFormData(initFormData({}));
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const fetchDocumentsData = async () => {
        try {
            if (id) {
                const documentDetails = await documentsPermitteesService.find(id);
                setAttachmentFileName(documentDetails.anexos || []);
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const toFormData = () => {
        const formValues = {
            id_permissionario: parentId,
            ...(id ? { conteudo: formData[ids.content].value } : {}),
            ...(id ? { conteudo_fields: JSON.parse(formData[ids.content].value) } : {}),
            tipo: formData[ids.documentType].value,
            data_validade: isoToBrFormat(formData[ids.documentExpirationDate].value),
            observacoes: formData[ids.documentObservations].value,
            marca_taximetro: formData[ids.taximeterBrand]?.value || null,
            modelo_taximetro: formData[ids.taximeterModel]?.value || null,
            numero_taximetro: formData[ids.taximeterNumber]?.value || null,
        };

        const data = new FormData();
        data.append("requestData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));

        const files = attachmentRef?.current?.getFiles?.();
        if (files && Array.isArray(files)) {
            files.forEach((file) => {
                data.append("anexos", file);
            });
        }

        return data;
    };

    const handleSubmit = async (e) => {
        if (isFormInvalid) return;

        try {
            const data = toFormData();

            if (id) {
                await documentsPermitteesService.update(data, id);
                showToast({ title: t("documents.messages.success.edit"), status: "success" });
            } else {
                await documentsPermitteesService.create(data);
                showToast({ title: t("documents.messages.success.create"), status: "success" });
            }

            navigate(`${ROUTES.permitteeDocumentsPath}/?parent_id=${parentId}`);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const applyDocumentTypeRules = (type, formData, ids) => {
        const updatedFormData = { ...formData };

        const docType = Object.values(DocumentPermitteeType).find(dt => dt.value === type);
        if (!docType) return updatedFormData;

        updatedFormData[ids.documentExpirationDate] = {
            ...updatedFormData[ids.documentExpirationDate],
            value: docType.expirationDate || '',
            validations:
                type === DocumentPermitteeType.LICENCE.value || type === DocumentPermitteeType.PERMIT_RENEWAL.value
                    ? [{ required: true }]
                    : [],
        };

        updatedFormData[ids.documentObservations] = {
            ...updatedFormData[ids.documentObservations],
            value: docType.observation,
        };

        return updatedFormData;
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const baseFormData = { ...formData, [id]: { ...formData[id], value } };

        const newFormData =
            id === ids.documentType
                ? applyDocumentTypeRules(value, baseFormData, ids)
                : baseFormData;

        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    const handleExpirationDateChange = (value) => {
        const formatted =
            moment(value).isValid()
                ? moment(value).format(DATE_FORMAT_ISO)
                : "";

        const newFormData = {
            ...formData,
            [ids.documentExpirationDate]: {
                ...formData[ids.documentExpirationDate],
                value: formatted,
            },
        };

        setFormData(newFormData);

        setInputError(
            checkErrors({
                formData: newFormData,
                inputError,
                name: ids.documentExpirationDate,
            })
        );
    };

    const handleInspectionChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;
        const contentValue = formData[ids.content].value

        if (id == ids.inspectionLicenseNumber) {
            const newValue = updateContentLicenseNumber(contentValue, value);
            setFormData({ ...formData, [ids.content]: { ...formData[ids.content], value: newValue} });
        } else if (id == ids.inspectionTaximeterNumber) {
            const newValue = updateContentTaximeterNumber(contentValue, value);
            setFormData({ ...formData, [ids.content]: { ...formData[ids.content], value: newValue} });
        }
    };

    const handleDownloadFile = async (fileName) => {
        try {
            await downloadFile(parentType, parentId, id, fileName)
        } catch (error) {
            showToast({
                title: t("system.messages.errors.attachment-download"),
                status: "error",
            });
        }
    };

    const handlePrintFile = async (fileName) => {
        try {
            await printFile(parentType, parentId, id, fileName)
        } catch (error) {
            showToast({
                title: t("system.messages.errors.print-unavailable"),
                status: "error",
            });
        }
    };

    const handleDeleteFile = async (fileName) => {
        try {
            await documentsService.deleteAttachment(parentType, parentId, id, fileName);
            fetchDocumentsData();
        } catch (error) {
            showToast({
                title: t("system.messages.errors.attachment-delete"),
                status: "error",
            });
        }
    }

    const fetchPermitteeData = async () => {
        if (!parentId) return;

        try {
            const data = await permitteesService.detail(parentId);
            setPermitteeData(data);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        if (permitteeData) {
            fetchData();
        }
    }, [permitteeData, id]);

    useEffect(() => {
        fetchPermitteeData();
    }, []);

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                {permitteeData && (
                    <PermitteesDocumentsListPageCardHeader permitteeData={permitteeData} />
                )}
                <ListPageCardHeader title={t(id ? 'documents.form.edit-title' : 'documents.form.title')} />
                <CardBody px="22px">
                    {formData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            {!id && (
                                <Stack spacing={formSpacing} direction={formDirection}>
                                    <FormControl isRequired>
                                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                            {t("documents.form.fields.type.label")}
                                        </FormLabel>
                                        <HStack align="center" justify="space-between">
                                            <SearchSelect
                                                required
                                                id={ids.documentType}
                                                value={formData[ids.documentType].value}
                                                isDisabled={!documentTypes.length}
                                                items={documentTypes}
                                                valueField="value"
                                                displayField="display"
                                                placeholder={t('documents.form.fields.type.placeholder')}
                                                onChange={(e) => {
                                                    handleInputChange(e);
                                                    setInputError({});
                                                }}
                                            />
                                        </HStack>
                                        <FormErrorMessage>
                                            {t(inputError[ids.documentDate])}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Stack>
                            )}

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired={
                                        formData[ids.documentType].value === DocumentPermitteeType.LICENCE.value ||
                                        formData[ids.documentType].value === DocumentPermitteeType.PERMIT_RENEWAL.value
                                    }
                                    isInvalid={
                                        (formData[ids.documentType].value === DocumentPermitteeType.LICENCE.value ||
                                            formData[ids.documentType].value === DocumentPermitteeType.PERMIT_RENEWAL.value) &&
                                        !!inputError[ids.documentExpirationDate]
                                    }
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.expiration-date.label")}
                                    </FormLabel>
                                    <DateTime
                                        id={ids.documentExpirationDate}
                                        locale={LOCALE_PT_BR}
                                        dateFormat={DATE_FORMAT}
                                        timeFormat={false}
                                        value={
                                            formData[ids.documentExpirationDate].value
                                                ? moment(formData[ids.documentExpirationDate].value, DATE_FORMAT_ISO)
                                                : ""
                                        }
                                        renderInput={(props) => (
                                            <Input
                                                {...props}
                                                fontSize="sm"
                                                variant="main"
                                                id={ids.documentExpirationDate}
                                            />
                                        )}
                                        onChange={handleExpirationDateChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentExpirationDate])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.documentObservations]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.observations.label")}
                                    </FormLabel>
                                    <Textarea
                                        minH="120px"
                                        bg={bgTextarea}
                                        id={ids.documentObservations}
                                        value={formData[ids.documentObservations].value}
                                        onChange={handleInputChange}
                                        _placeholder={{ color: placeholderColor }}
                                        placeholder={t('documents.form.fields.observations.placeholder')}
                                        fontSize="xs"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentObservations])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <FileAttachmentMultipleInput
                                ref={attachmentRef}
                                nameVariable={fileAttachmentName}
                                onDownloadFile={handleDownloadFile}
                                onPrintFile={handlePrintFile}
                                onDeleteFile={handleDeleteFile}
                                acceptedTypes={['application/pdf', 'image/jpeg']}/>

                            {[DocumentPermitteeType.TAXIMETER_INSTALLATION.value, DocumentPermitteeType.TAXIMETER_VERIFICATION.value].includes(formData?.[ids.documentType]?.value) && (
                                <>
                                    <ListPageCardHeader px={0} title={t(id ? 'permittees.documents.form.taximeter.edit-title' : 'permittees.documents.form.taximeter.title')} />

                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl isInvalid={inputError[ids.taximeterBrand]}>
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t("permittees.documents.form.taximeter.fields.brand.label")}
                                            </FormLabel>
                                            <Input
                                                id={ids.taximeterBrand}
                                                value={formData[ids.taximeterBrand].value}
                                                onChange={handleInputChange}
                                                _placeholder={{ color: placeholderColor }}
                                                placeholder={t('permittees.documents.form.taximeter.fields.brand.placeholder')}
                                                fontSize="xs"
                                            />
                                            <FormErrorMessage>{t(inputError[ids.taximeterBrand])}</FormErrorMessage>
                                        </FormControl>
                                    </Stack>

                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl isInvalid={inputError[ids.taximeterModel]}>
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t("permittees.documents.form.taximeter.fields.model.label")}
                                            </FormLabel>
                                            <Input
                                                id={ids.taximeterModel}
                                                value={formData[ids.taximeterModel].value}
                                                onChange={handleInputChange}
                                                _placeholder={{ color: placeholderColor }}
                                                placeholder={t('permittees.documents.form.taximeter.fields.model.placeholder')}
                                                fontSize="xs"
                                            />
                                            <FormErrorMessage>{t(inputError[ids.taximeterModel])}</FormErrorMessage>
                                        </FormControl>
                                    </Stack>

                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl isInvalid={inputError[ids.taximeterNumber]}>
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t("permittees.documents.form.taximeter.fields.number.label")}
                                            </FormLabel>
                                            <Input
                                                id={ids.taximeterNumber}
                                                value={formData[ids.taximeterNumber].value}
                                                onChange={handleInputChange}
                                                _placeholder={{ color: placeholderColor }}
                                                placeholder={t('permittees.documents.form.taximeter.fields.number.placeholder')}
                                                fontSize="xs"
                                            />
                                            <FormErrorMessage>{t(inputError[ids.taximeterNumber])}</FormErrorMessage>
                                        </FormControl>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>

            {formData && formData[ids.documentType].value == "VISTORIA" && (
                <Card mt={"20px"}>
                    <ListPageCardHeader title={t('documents.form.inspection-data-title')} />

                    <CardBody px="22px">
                        <Stack spacing={formSpacing} direction={formDirection}>
                            <FormControl isInvalid={inputError[ids.inspectionLicenseNumber]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t("documents.form.fields.license-number.label")}
                                </FormLabel>
                                <Input
                                    id={ids.inspectionLicenseNumber}
                                    value={getLicenseNumberFromContent(formData[ids.content].value)}
                                    onChange={handleInspectionChange}
                                    _placeholder={{ color: placeholderColor }}
                                    fontSize="xs"
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.inspectionLicenseNumber])}
                                </FormErrorMessage>
                            </FormControl>

                            {permitteeData.cab && (
                                <FormControl isInvalid={inputError[ids.inspectionTaximeterNumber]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.taximeter-number.label")}
                                    </FormLabel>
                                    <Input
                                        id={ids.inspectionTaximeterNumber}
                                        value={getTaximeterNumberFromContent(formData[ids.content].value)}
                                        onChange={handleInspectionChange}
                                        _placeholder={{ color: placeholderColor }}
                                        fontSize="xs"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.inspectionTaximeterNumber])}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                        </Stack>
                    </CardBody>
                </Card>
            )}

            <Flex mt={"20px"} w="100%" justify="space-between">
                <Button
                    variant="no-effects"
                    alignSelf="flex-end"
                    w="125px"
                    bg="gray.100"
                    color="gray.700"
                    fontSize="sm"
                    fontWeight="bold"
                    onClick={() => navigate(`${ROUTES.permitteeDocumentsPath}/?parent_id=${parentId}`)}
                >
                    {t("system.actions.cancel")}
                </Button>
                <Button
                    w="100px"
                    variant="dark"
                    fontSize="sm"
                    onClick={handleSubmit}
                    _hover={{
                        bg: "grey.500",
                        color: "white"
                    }}
                    isDisabled={isFormInvalid}
                >
                    {t(id ? 'system.actions.edit' : 'system.actions.save')}
                </Button>
            </Flex>
        </Flex >
    );
};

export default PermitteesDocumentsNew;
